import { useMemo, useRef, useState, type DragEvent, type ReactNode } from 'react';
import {
  RADIO_TECHNOLOGIES,
  assignSequence,
  isParseFailure,
  locationLabel,
  parseSnyperReport,
  type LocationNode,
  type MeasurementSeries,
  type ParsedReport,
  type SequencedReport,
} from '@oscom/shared';
import { importMeasurementSeries } from '../api/measurements';

const LABEL_MAX_LENGTH = 120;
const MAX_FILES = 100;
const MAX_FILE_BYTES = 1024 * 1024;
const FILE_NAME_PATTERN = /^[\w.\- ]{1,120}\.csv$/;

interface SelectedFile {
  name: string;
  content: string;
}

interface FailedFile {
  fileName: string;
  error: string;
}

interface Analysis {
  sequenced: SequencedReport[];
  failures: FailedFile[];
}

/** `2026-01-15T09:30:00` -> `15.01.2026 09:30` */
export function formatMeasuredAt(measuredAt: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(measuredAt);
  return match ? `${match[3]}.${match[2]}.${match[1]} ${match[4]}:${match[5]}` : measuredAt;
}

function cellSummary(report: ParsedReport): string {
  return RADIO_TECHNOLOGIES.map((technology) => ({
    technology,
    count: report.cells.filter((cell) => cell.technology === technology).length,
  }))
    .filter((entry) => entry.count > 0)
    .map((entry) => `${entry.technology} ${entry.count}`)
    .join(' · ');
}

/** Cheap pre-checks; the API stays authoritative and repeats them. */
function precheck(file: File): string | undefined {
  if (!FILE_NAME_PATTERN.test(file.name)) {
    return 'Der Dateiname ist nicht zulässig (nur Buchstaben, Ziffern, Leerzeichen, „.“, „-“, „_“; Endung .csv).';
  }
  if (file.size > MAX_FILE_BYTES) return 'Die Datei ist größer als 1 MB.';
  return undefined;
}

function analyse(files: readonly SelectedFile[], rejected: readonly FailedFile[]): Analysis {
  const reports: ParsedReport[] = [];
  const failures: FailedFile[] = [...rejected];
  const seenNames = new Set<string>();
  const firstByContent = new Map<string, string>();
  for (const file of files) {
    if (seenNames.has(file.name)) {
      failures.push({ fileName: file.name, error: 'Dateiname doppelt.' });
      continue;
    }
    seenNames.add(file.name);
    const duplicateOf = firstByContent.get(file.content);
    if (duplicateOf !== undefined) {
      failures.push({ fileName: file.name, error: `Inhalt identisch mit ${duplicateOf}.` });
      continue;
    }
    firstByContent.set(file.content, file.name);
    const result = parseSnyperReport(file.name, file.content);
    if (isParseFailure(result)) failures.push(result);
    else reports.push(result);
  }
  return { sequenced: assignSequence(reports), failures };
}

export interface MeasurementImportDialogProps {
  customerId: string;
  locations: readonly LocationNode[];
  onClose: () => void;
  onImported: (series: MeasurementSeries) => void;
}

/**
 * Import dialog: choose reports, preview the numbering the server will assign
 * (same `assignSequence`), then send the RAW contents - the API re-parses and
 * decides, the preview only spares a round trip.
 */
export function MeasurementImportDialog({
  customerId,
  locations,
  onClose,
  onImported,
}: MeasurementImportDialogProps): ReactNode {
  const [label, setLabel] = useState('');
  const [siteLocationId, setSiteLocationId] = useState('');
  const [selected, setSelected] = useState<SelectedFile[]>([]);
  const [rejected, setRejected] = useState<FailedFile[]>([]);
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const readCounter = useRef(0);

  const sites = locations.filter((node) => node.locationType === 'site');
  const { sequenced, failures } = useMemo(() => analyse(selected, rejected), [selected, rejected]);
  const trimmedLabel = label.trim();
  const fileCount = selected.length + rejected.length;
  const tooMany = fileCount > MAX_FILES;
  const canSubmit =
    !submitting &&
    trimmedLabel !== '' &&
    trimmedLabel.length <= LABEL_MAX_LENGTH &&
    selected.length > 0 &&
    failures.length === 0 &&
    !tooMany;

  async function readFiles(list: readonly File[]): Promise<void> {
    const ticket = ++readCounter.current;
    setError(undefined);
    const accepted: SelectedFile[] = [];
    const refused: FailedFile[] = [];
    for (const file of list) {
      const problem = precheck(file);
      if (problem) {
        refused.push({ fileName: file.name, error: problem });
        continue;
      }
      try {
        accepted.push({ name: file.name, content: await file.text() });
      } catch {
        refused.push({ fileName: file.name, error: 'Die Datei konnte nicht gelesen werden.' });
      }
    }
    // A newer selection supersedes a slower read.
    if (ticket !== readCounter.current) return;
    setSelected(accepted);
    setRejected(refused);
  }

  function handleDrop(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    setDragging(false);
    void readFiles([...event.dataTransfer.files]);
  }

  async function handleSubmit(): Promise<void> {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(undefined);
    try {
      const response = await importMeasurementSeries(customerId, {
        label: trimmedLabel,
        ...(siteLocationId ? { siteLocationId } : {}),
        files: selected.map((file) => ({ name: file.name, content: file.content })),
      });
      onImported(response.series);
    } catch (caught: unknown) {
      setError(caught instanceof Error ? caught.message : 'Import fehlgeschlagen.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="card" role="dialog" aria-modal="false" aria-labelledby="import-title">
      <h2 id="import-title">Messdateien importieren</h2>
      <form
        className="stack"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        <div className="form-field">
          <label htmlFor="import-label">Bezeichnung</label>
          <input
            id="import-label"
            type="text"
            maxLength={LABEL_MAX_LENGTH}
            aria-required="true"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="import-site">Standort</label>
          <select
            id="import-site"
            value={siteLocationId}
            onChange={(event) => setSiteLocationId(event.target.value)}
          >
            <option value="">(kein Standort)</option>
            {sites.map((node) => (
              <option key={node.id} value={node.id}>
                {locationLabel(node)}
              </option>
            ))}
          </select>
        </div>

        <div
          className={dragging ? 'dropzone dropzone--active' : 'dropzone'}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          <label htmlFor="import-files">Messdateien (CSV) auswählen oder hierher ziehen</label>
          <input
            id="import-files"
            type="file"
            accept=".csv"
            multiple
            onChange={(event) => void readFiles([...(event.target.files ?? [])])}
          />
          <p className="muted">1 bis {MAX_FILES} Dateien, je höchstens 1 MB.</p>
        </div>

        {tooMany ? (
          <p className="notice notice--warning">
            Es können höchstens {MAX_FILES} Dateien auf einmal importiert werden.
          </p>
        ) : null}

        {fileCount > 0 ? (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Nr.</th>
                  <th scope="col">Datei</th>
                  <th scope="col">Zeitpunkt</th>
                  <th scope="col">Zyklen</th>
                  <th scope="col">Zellen</th>
                  <th scope="col">Hinweis</th>
                </tr>
              </thead>
              <tbody>
                {sequenced.map((report, index) => (
                  <tr key={`report-${index}`}>
                    <td>{report.sequenceNumber}</td>
                    <td>{report.fileName}</td>
                    <td>{formatMeasuredAt(report.measuredAt)}</td>
                    <td>{`${report.cycles.completed}/${report.cycles.target}`}</td>
                    <td>{cellSummary(report)}</td>
                    <td>{report.warnings.join(' ')}</td>
                  </tr>
                ))}
                {failures.map((failure, index) => (
                  <tr key={`error-${index}`} className="import-row--error">
                    <td />
                    <td>{failure.fileName}</td>
                    <td colSpan={4}>{failure.error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {error ? (
          <p className="notice notice--warning notice--multiline" role="alert">
            {error}
          </p>
        ) : null}

        <p>
          <button className="button button--primary" type="submit" disabled={!canSubmit}>
            Importieren
          </button>{' '}
          <button
            type="button"
            className="button button--ghost"
            disabled={submitting}
            onClick={onClose}
          >
            Abbrechen
          </button>
        </p>
      </form>
    </div>
  );
}
